const TOKEN_KEY = 'zhengming_session'
const ADMIN_TICKET_KEY = 'zhengming_admin_ticket'

declare global {
  interface Window {
    ZHENGMING_CONFIG?: { WORKER_ORIGIN?: string }
  }
}

const API_ORIGIN = String(window.ZHENGMING_CONFIG?.WORKER_ORIGIN || '').replace(/\/+$/, '')
export const apiUrl = (path: string) => `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`

const toBase64 = (value: ArrayBuffer | Uint8Array) => {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value)
  let binary = ''
  bytes.forEach(byte => { binary += String.fromCharCode(byte) })
  return btoa(binary)
}

async function encryptCredential(password: string) {
  if (!window.isSecureContext || !crypto.subtle) throw new Error('SECURE_CONTEXT_REQUIRED')
  const exchange = await api<{ keyId:string; publicKey:string }>('/api/auth/key')
  const serverKey = await crypto.subtle.importKey('jwk', JSON.parse(exchange.publicKey), { name:'ECDH', namedCurve:'P-256' }, false, [])
  const clientKeys = await crypto.subtle.generateKey({ name:'ECDH', namedCurve:'P-256' }, true, ['deriveKey'])
  const aesKey = await crypto.subtle.deriveKey({ name:'ECDH', public:serverKey }, clientKeys.privateKey, { name:'AES-GCM', length:256 }, false, ['encrypt'])
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherText = await crypto.subtle.encrypt({ name:'AES-GCM', iv }, aesKey, new TextEncoder().encode(password))
  const clientPublicKey = await crypto.subtle.exportKey('jwk', clientKeys.publicKey)
  return { keyId:exchange.keyId, clientPublicKey, iv:toBase64(iv), cipherText:toBase64(cipherText), credentialVersion:1 }
}

export const session = {
  token: () => localStorage.getItem(TOKEN_KEY) || '',
  save: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(ADMIN_TICKET_KEY) },
  adminTicket: () => localStorage.getItem(ADMIN_TICKET_KEY) || '',
  saveAdminTicket: (ticket: string) => localStorage.setItem(ADMIN_TICKET_KEY, ticket),
  clearAdminTicket: () => localStorage.removeItem(ADMIN_TICKET_KEY)
}

export async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = session.token()
  const ticket = session.adminTicket()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (ticket && path.startsWith('/api/admin/') && path !== '/api/admin/gate') headers.set('X-Admin-Gate', ticket)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type') && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json')
  }
  const response = await fetch(apiUrl(path), { ...init, headers })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    if (payload?.error === 'MOVED' && payload?.location) {
      location.hash = payload.location.startsWith('#') ? payload.location : `#${payload.location}`
      throw new Error('内容地址已经更新，正在跳转。')
    }
    const parts = [payload?.error, payload?.detail || payload?.message].filter(Boolean)
    throw new Error(parts.length ? parts.join('：') : `HTTP_${response.status}`)
  }
  return payload as T
}

export async function upload(file: File, purpose: 'avatar'|'content'|'comment'|'music'|'download') {
  const maxBytes = purpose === 'avatar' ? 5 * 1024 * 1024
    : purpose === 'comment' ? 8 * 1024 * 1024
    : purpose === 'content' ? 15 * 1024 * 1024
    : 95 * 1024 * 1024
  if (file.size > maxBytes) throw new Error(`FILE_TOO_LARGE：当前文件 ${(file.size / 1024 / 1024).toFixed(1)}MB，最大 ${(maxBytes / 1024 / 1024).toFixed(0)}MB`)

  const headers = new Headers({
    'X-Upload-Purpose': purpose,
    'X-File-Name': encodeURIComponent(file.name || 'upload.bin'),
    'X-File-Type': file.type || 'application/octet-stream',
    'X-File-Size': String(file.size)
  })
  const token = session.token()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const send = async (url: string) => {
    const response = await fetch(url, { method: 'POST', headers, body: file })
    const payload = await response.json().catch(() => null)
    if (!response.ok) throw new Error([payload?.error, payload?.detail || payload?.message].filter(Boolean).join('：') || `UPLOAD_HTTP_${response.status}`)
    return payload
  }

  try {
    return await send(apiUrl('/api/uploads/raw'))
  } catch (error: any) {
    const directOrigin = window.ZHENGMING_CONFIG?.WORKER_ORIGIN?.replace(/\/+$/, '')
    if (!directOrigin || !/Failed to fetch|NETWORK|UPSTREAM_UNAVAILABLE/i.test(String(error?.message || error))) throw error
    return send(`${directOrigin}/api/uploads/raw`)
  }
}

export async function compressAvatar(file: File, size = 512, quality = 0.84): Promise<File> {
  if (file.type === 'image/gif') return file
  const image = await createImageBitmap(file)
  const scale = Math.min(1, size / Math.max(image.width, image.height))
  const width = Math.max(1, Math.round(image.width * scale))
  const height = Math.max(1, Math.round(image.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { alpha: false })
  if (!context) throw new Error('CANVAS_UNAVAILABLE')
  context.fillStyle = '#f1eee5'
  context.fillRect(0, 0, width, height)
  context.drawImage(image, 0, 0, width, height)
  image.close()
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(value => value ? resolve(value) : reject(new Error('COMPRESS_FAILED')), 'image/webp', quality)
  )
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.webp', { type: 'image/webp' })
}

export const authApi = {
  register: async (username: string, password: string, displayName: string) => api('/api/auth/register', {
    method: 'POST', body: JSON.stringify({ username, displayName, credential: await encryptCredential(password) })
  }),
  login: async (username: string, password: string) => {
    const result = await api<{token:string;user:any}>('/api/auth/login', {
      method: 'POST', body: JSON.stringify({ username, credential: await encryptCredential(password) })
    })
    session.save(result.token)
    return result
  },
  logout: async () => {
    try { await api('/api/auth/logout', { method: 'POST' }) } catch { /* 会话可能已在注销账号时撤销 */ } finally { session.clear() }
  },
  me: () => api('/api/me'),
  updateProfile: (displayName: string, username?: string) => api('/api/me/account', {
    method: 'PATCH', body: JSON.stringify({ displayName, username })
  }),
  changePassword: async (currentPassword: string, newPassword: string) => api('/api/me/password', {
    method: 'PATCH', body: JSON.stringify({ currentCredential: await encryptCredential(currentPassword), newCredential: await encryptCredential(newPassword) })
  }),
  remove: () => api('/api/me', { method: 'DELETE' })
}

export const contentApi = {
  list: (type?: string, query?: string) => api(`/api/works?${new URLSearchParams({
    ...(type ? {type} : {}), ...(query ? {q:query} : {})
  })}`),
  search: (query: string) => api(`/api/search?q=${encodeURIComponent(query)}`),
  tagCloud: (page = 1, limit = 36) => api(`/api/tags/cloud?page=${page}&limit=${limit}`),
  tagWorks: (slug: string, page = 1, limit = 8) => api(`/api/tags/${encodeURIComponent(slug)}/works?page=${page}&limit=${limit}`),
  detail: (slug: string) => api(`/api/works/${encodeURIComponent(slug)}`),
  view: (id: string) => api(`/api/works/${id}/view`, { method: 'POST' }),
  comments: (id: string) => api(`/api/works/${id}/comments`),
  comment: (id: string, text: string, parentId?: string, imageId?: string) => api(`/api/works/${id}/comments`, {
    method: 'POST', body: JSON.stringify({ text, parentId, imageId })
  }),
  react: (commentId: string, reaction: 'like'|'dislike') => api(`/api/comments/${commentId}/react`, {
    method: 'POST', body: JSON.stringify({ reaction })
  }),
  deleteComment: (commentId: string) => api(`/api/comments/${commentId}`, { method: 'DELETE' }),
  rate: (id: string, score: number) => api(`/api/works/${id}/rating`, {
    method: 'POST', body: JSON.stringify({ score })
  }),
  favorite: (id: string) => api(`/api/me/favorites/${id}`, { method: 'POST' }),
  unfavorite: (id: string) => api(`/api/me/favorites/${id}`, { method: 'DELETE' }),
  progress: (id: string, progress: number, chapterId?: string, locator?: unknown) => api(`/api/me/progress/${id}`, {
    method: 'PUT', body: JSON.stringify({ progress, chapterId, locator })
  })
}

export const musicApi = {
  list: () => api('/api/music')
}

export const forumApi = {
  topics: (page = 1) => api(`/api/forum/topics?page=${page}`),
  topic: (slug:string) => api(`/api/forum/topics/${encodeURIComponent(slug)}`),
  createTopic: (title:string, body:string) => api('/api/forum/topics', { method:'POST',body:JSON.stringify({title,body}) }),
  reply: (id:string, text:string, parentId?:string) => api(`/api/forum/topics/${id}/replies`, { method:'POST',body:JSON.stringify({text,parentId}) }),
  deleteReply: (id:string) => api(`/api/comments/${id}`, {method:'DELETE'})
}

export const feedbackApi = {
  create: (payload: unknown) => api('/api/feedback', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
  mine: () => api('/api/me/feedback')
}

export const submitApi = {
  create: (submission: unknown) => api('/api/submissions', { method: 'POST', body: JSON.stringify(submission) }),
  aiMetadata: (payload: string | { text?: string; title?: string; author?: string; fileName?: string; type?: string }) => api('/api/ai/metadata', {
    method: 'POST', body: JSON.stringify(typeof payload === 'string' ? { text: payload } : payload)
  }),
  pdfMetadata: (attachmentId:string, pageCount:number, text:string) => api(`/api/uploads/${attachmentId}/pdf-metadata`, {
    method:'PUT', body:JSON.stringify({ pageCount, text })
  })
}

export const adminApi = {
  unlock: async (key: string) => {
    const result = await api<{ticket:string}>('/api/admin/gate', { method:'POST', headers:{'X-Admin-Gate':key} })
    session.saveAdminTicket(result.ticket)
    return result
  },
  users: () => api('/api/admin/users'),
  updateUser: (id:string, patch:unknown) => api(`/api/admin/users/${id}`, {method:'PATCH',body:JSON.stringify(patch)}),
  summary: () => api('/api/admin/summary'),
  submissions: () => api('/api/admin/submissions'),
  review: (id:string,status:string,note='') => api(`/api/admin/submissions/${id}`, {
    method:'PATCH',body:JSON.stringify({status,note})
  }),
  works: () => api('/api/admin/works'),
  createWork: (payload:unknown) => api('/api/admin/works', {method:'POST',body:JSON.stringify(payload)}),
  workDetail: (id:string) => api(`/api/admin/works/${id}`),
  music: () => api('/api/admin/music'),
  updateWork: (id:string, patch:unknown) => api(`/api/admin/works/${id}`, {method:'PATCH',body:JSON.stringify(patch)}),
  deleteWork: (id:string) => api(`/api/admin/works/${id}`, {method:'DELETE'}),
  comments: () => api('/api/admin/comments'),
  deleteComment: (id:string) => api(`/api/admin/comments/${id}`, {method:'DELETE'}),
  feedback: () => api('/api/admin/feedback'),
  updateFeedback: (id:string, patch:unknown) => api(`/api/admin/feedback/${id}`, {
    method:'PATCH',
    body:JSON.stringify(patch)
  }),
  audit: () => api('/api/admin/audit'),
  createMusic: (payload:unknown) => api('/api/admin/music', {method:'POST',body:JSON.stringify(payload)}),
  updateMusic: (id:string, payload:unknown) => api(`/api/admin/music/${id}`, {method:'PATCH',body:JSON.stringify(payload)}),
  deleteMusic: (id:string) => api(`/api/admin/music/${id}`, {method:'DELETE'}),
  createRouteAlias: (oldPath:string,targetPath:string) => api('/api/admin/routes/alias', {method:'POST',body:JSON.stringify({oldPath,targetPath})})
}